/**
 * Route guard for the operator SPA (Phase 5b) — replaces
 * `pages/AdminGate.tsx`'s shared-password screen. Wraps every
 * operator-facing route in `App.tsx`; public driver/viewer routes
 * (`/share/.../viewer`, `/join/<CODE>`, `/event/<id>`, `/login`,
 * `/invite/<token>`) are NOT wrapped by this — App.tsx unwraps them
 * before reaching it, same as it did for `AdminGate`.
 *
 * Unlike `AdminGate` (a client-side-only "friction layer" the plan's
 * own docstring called out as "NOT real authentication" — the backend
 * endpoints were unauthenticated regardless), this is a real gate
 * layered on top of REAL server-side enforcement
 * (`services/auth/policy.py::require_authz`, wired across the control
 * plane since the Phase 4 enforcement sweep): every API call an
 * operator route makes is independently checked server-side, so this
 * component is a UX nicety (don't render a broken dashboard, bounce
 * to /login immediately) rather than the actual security boundary.
 *
 * "Has a session" = `principal.kind === 'user'` — deliberately not
 * `principal.is_superadmin` (an organizer without superadmin still
 * needs the operator routes for their own events) and not
 * `'operator_legacy'` (the backend's break-glass bearer bridge; the
 * SPA itself never presents that credential post-Phase-5b, so
 * `whoami` never resolves to it for a browser session — see
 * `state/authSession.tsx`).
 */

import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Lock, LogOut } from 'lucide-react';

import { useAuthSession } from '@/state/authSession';

export const RequireSession: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { principal, loading } = useAuthSession();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-base">
        <Lock className="size-6 animate-pulse text-fg-secondary" />
      </div>
    );
  }

  if (!principal || principal.kind !== 'user') {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  return <>{children}</>;
};

export default RequireSession;

/**
 * Sign-out affordance for the Navbar — replaces `AdminGate.tsx`'s
 * `AdminSignOutButton` (same className API, same rendered shape).
 * Renders nothing when there's no real session (matches the old
 * component's "renders nothing when the gate is disabled" behavior).
 */
export const SignOutButton: React.FC<{ className?: string }> = ({ className }) => {
  const { principal, logout } = useAuthSession();
  const navigate = useNavigate();

  if (!principal || principal.kind !== 'user') return null;

  const handleSignOut = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <button
      type="button"
      onClick={() => void handleSignOut()}
      title="Sign out"
      aria-label="Sign out of admin"
      className={
        className ??
        'flex items-center gap-1 whitespace-nowrap rounded px-2 py-1 text-xs text-fg-secondary hover:bg-bg-surface hover:text-fg'
      }
    >
      <LogOut className="size-3.5" />
      Sign out
    </button>
  );
};
